import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableHead,
    DataTableHeader,
    DataTableHeaderRow,
    DataTableRow,
} from '@/components/system';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
    BILLING_QUOTA_FEATURES,
    formatEntitlementDate,
    formatEntitlementNumber,
    getEntitlementUsage,
    isQuotaEntitlement,
    type BillingQuotaFeature,
    type EntitlementUsage,
} from '@/lib/entitlements';
import { cn } from '@/lib/utils';
import type { EntitlementDecision, Entitlements } from '@/api/entitlements';

type QuotaUsageTableProps = {
    entitlements?: Entitlements | null;
    isLoading: boolean;
    isError: boolean;
};

type QuotaStatus = {
    label: string;
    variant: 'success' | 'warning' | 'destructive' | 'neutral';
    progressClassName: string;
};

const getUnitLabel = (value: number, feature: BillingQuotaFeature) =>
    value === 1 ? feature.singularUnitLabel : feature.unitLabel;

const formatValueWithUnit = (value: number, feature: BillingQuotaFeature) =>
    `${formatEntitlementNumber(value)} ${getUnitLabel(value, feature)}`;

const getUsagePercent = (usage?: EntitlementUsage | null) => {
    const limit = usage?.limit ?? 0;
    const used = usage?.used ?? 0;

    return limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
};

const getUsageLabel = (decision: EntitlementDecision | undefined, feature: BillingQuotaFeature) => {
    if (!isQuotaEntitlement(decision)) return 'Indisponível';

    const used = decision.used ?? 0;

    if (decision.limit === null) {
        return `${formatValueWithUnit(used, feature)} usados`;
    }

    const limit = decision.limit ?? 0;

    return `${formatEntitlementNumber(used)} de ${formatValueWithUnit(limit, feature)}`;
};

const getRemainingLabel = (
    decision: EntitlementDecision | undefined,
    feature: BillingQuotaFeature,
    entitlements?: Entitlements | null,
) => {
    if (!entitlements?.active) return 'Plano inativo';
    if (!isQuotaEntitlement(decision)) return '-';
    if (decision.remaining === null) return 'Sem limite';

    return formatValueWithUnit(decision.remaining ?? 0, feature);
};

const getRenewalLabel = (
    decision: EntitlementDecision | undefined,
    entitlements?: Entitlements | null,
) => {
    if (!entitlements?.active) return 'Não renova';

    const renewalDate = formatEntitlementDate(decision?.renews_at);
    return renewalDate ?? 'Sem renovação';
};

const getQuotaStatus = (
    decision: EntitlementDecision | undefined,
    entitlements?: Entitlements | null,
): QuotaStatus => {
    if (!decision) {
        return {
            label: 'Indisponível',
            variant: 'neutral',
            progressClassName: 'bg-border-strong',
        };
    }

    if (!entitlements?.active) {
        return {
            label: 'Plano inativo',
            variant: 'destructive',
            progressClassName: 'bg-destructive',
        };
    }

    if (decision.reason === 'quota_exceeded') {
        return {
            label: 'No limite',
            variant: 'warning',
            progressClassName: 'bg-warning',
        };
    }

    if (!decision.allowed) {
        return {
            label: 'Indisponível',
            variant: 'destructive',
            progressClassName: 'bg-destructive',
        };
    }

    return {
        label: 'Disponível',
        variant: 'success',
        progressClassName: 'bg-brand',
    };
};

const LoadingRows = () => (
    <>
        {BILLING_QUOTA_FEATURES.map((feature) => (
            <DataTableRow key={feature.feature}>
                <DataTableCell className="min-w-[16rem]">
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-3 w-52" />
                    </div>
                </DataTableCell>
                <DataTableCell className="min-w-[14rem]">
                    <div className="space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-2 w-40 rounded-full" />
                    </div>
                </DataTableCell>
                <DataTableCell variant="subtle">
                    <Skeleton className="h-4 w-20" />
                </DataTableCell>
                <DataTableCell variant="subtle">
                    <Skeleton className="h-4 w-24" />
                </DataTableCell>
                <DataTableCell className="text-right">
                    <Skeleton className="ml-auto h-6 w-24 rounded-full" />
                </DataTableCell>
            </DataTableRow>
        ))}
    </>
);

export default function QuotaUsageTable({
    entitlements,
    isLoading,
    isError,
}: QuotaUsageTableProps) {
    if (isError) {
        return (
            <div className="rounded-xl border border-destructive/20 bg-destructive/10 p-4 text-sm text-destructive">
                Não foi possível carregar os limites do seu plano agora.
            </div>
        );
    }

    return (
        <DataTable>
            <DataTableHeader>
                <DataTableHeaderRow>
                    <DataTableHead className="min-w-[16rem]">Recurso</DataTableHead>
                    <DataTableHead className="min-w-[14rem]">Uso</DataTableHead>
                    <DataTableHead className="min-w-[9rem]">Restante</DataTableHead>
                    <DataTableHead className="min-w-[10rem]">Renovação</DataTableHead>
                    <DataTableHead className="min-w-[9rem] text-right">Status</DataTableHead>
                </DataTableHeaderRow>
            </DataTableHeader>
            <DataTableBody>
                {isLoading ? (
                    <LoadingRows />
                ) : (
                    BILLING_QUOTA_FEATURES.map((feature) => {
                        const decision = entitlements?.features[feature.feature];
                        const usage = getEntitlementUsage(decision);
                        const status = getQuotaStatus(decision, entitlements);

                        return (
                            <DataTableRow key={feature.feature}>
                                <DataTableCell className="align-top">
                                    <div className="space-y-1">
                                        <p className="font-semibold text-foreground">
                                            {feature.label}
                                        </p>
                                        <p className="max-w-sm text-xs leading-5 text-muted-foreground">
                                            {feature.description}
                                        </p>
                                    </div>
                                </DataTableCell>
                                <DataTableCell className="align-top">
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium text-foreground">
                                            {getUsageLabel(decision, feature)}
                                        </p>
                                        <Progress
                                            value={getUsagePercent(usage)}
                                            className="h-2 bg-surface-muted"
                                            indicatorClassName={cn(
                                                'transition-transform duration-300',
                                                status.progressClassName,
                                            )}
                                        />
                                    </div>
                                </DataTableCell>
                                <DataTableCell variant="subtle" className="align-top">
                                    {getRemainingLabel(decision, feature, entitlements)}
                                </DataTableCell>
                                <DataTableCell variant="subtle" className="align-top">
                                    {getRenewalLabel(decision, entitlements)}
                                </DataTableCell>
                                <DataTableCell className="text-right align-top">
                                    <Badge variant={status.variant}>{status.label}</Badge>
                                </DataTableCell>
                            </DataTableRow>
                        );
                    })
                )}
            </DataTableBody>
        </DataTable>
    );
}
